/**
 * Sequentially upload a batch of pre-staged ``File`` objects against
 * a freshly-created project's run, surface failures as count-based
 * toasts, and return the IDs of the files that successfully landed.
 *
 * Lives here (not inline in the landing page) so it has a clean
 * unit-test surface — the page itself is otherwise mostly markup +
 * effects that drag in Tauri capability probes, the auth provider,
 * and the chat layout.
 *
 * Concurrency is bound to 1: a 20-file × 10 MB cap is at most ~200
 * MB which serial-uploads in a few seconds for realistic CSV/PDF
 * sizes, and the simpler control flow is easier to reason about
 * than a parallel ``Promise.all`` with partial-failure semantics.
 *
 * Toast copy and log fields deliberately omit the raw filename:
 * clinical filenames frequently encode PHI (patient name, DOB,
 * MRN). ``toast`` forwards destructive descriptions to telemetry
 * and ``logger`` ships to centralised logging, so anything here
 * becomes a compliance leak.
 */

import { toast } from '@heidi/design-system/toast';
import { logger, sanitizeError } from '@heidi/runtime/logger';

import type { ProjectAttachmentResponse } from '../../contracts/projects/requests.types';
import { projectsClient } from '../../data/api/projects';

interface UploadStagedFilesOptions {
  projectId: string;
  files: readonly File[];
  /** Override for tests / Storybook so we don't hit the real API. */
  uploadAttachment?: typeof projectsClient.uploadAttachment;
}

const fileExt = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
};

export const uploadStagedFiles = async ({
  projectId,
  files,
  uploadAttachment = projectsClient.uploadAttachment,
}: UploadStagedFilesOptions): Promise<ProjectAttachmentResponse[]> => {
  const uploaded: ProjectAttachmentResponse[] = [];
  let failed = 0;
  for (const file of files) {
    try {
      const response = await uploadAttachment({ projectId, file });
      uploaded.push(response.data);
    } catch (err) {
      failed += 1;
      // A fixed message: the upload error can echo the filename or the
      // server's response body, and the message ships verbatim.
      logger.error('Failed to upload landing-composer attachment', {
        projectId,
        fileSize: file.size,
        fileExt: fileExt(file.name),
        error: sanitizeError(err),
      });
    }
  }
  if (failed > 0) {
    toast({
      title: failed === 1 ? "Couldn't attach file" : "Couldn't attach files",
      description:
        failed === 1
          ? 'One attachment was skipped; the rest of your project will still be created.'
          : `${failed} attachments were skipped; the rest of your project will still be created.`,
      tone: 'destructive',
    });
  }
  return uploaded;
};
